import axios from "axios";
import { alertController } from "@ionic/vue";
import { FileOpener } from "@ionic-native/file-opener";
import { FileTransfer } from "@awesome-cordova-plugins/file-transfer";
import { File } from "@awesome-cordova-plugins/file";
import { AppVersion } from "@ionic-native/app-version";
import myConfig from "./config";
import { initSystemVersion } from "@/js/tools";

const PROGRESS_OVERLAY_ID = "apk-update-progress-overlay";

function buildProgressHtml(percent: number, statusText: string) {
  const safePercent = Math.max(0, Math.min(100, percent));
  return `
    <div style="position:fixed;inset:0;z-index:99999;background:rgba(0,0,0,.45);display:flex;align-items:center;justify-content:center;padding:24px;box-sizing:border-box;">
      <div style="width:min(320px,100%);background:#fff;border-radius:12px;padding:20px 18px 16px;box-shadow:0 8px 28px rgba(0,0,0,.18);">
        <div style="font-size:16px;font-weight:600;color:#222;text-align:center;margin-bottom:14px;">版本更新</div>
        <div style="height:10px;background:#e8e8e8;border-radius:5px;overflow:hidden;">
          <div style="height:100%;width:${safePercent}%;background:#3880ff;border-radius:5px;transition:width .15s linear;"></div>
        </div>
        <div style="margin-top:12px;text-align:center;font-size:15px;color:#333;">${safePercent}%</div>
        <div style="margin-top:6px;text-align:center;font-size:12px;color:#888;">${statusText}</div>
      </div>
    </div>
  `;
}

function showProgressOverlay(percent: number, statusText: string) {
  let el = document.getElementById(PROGRESS_OVERLAY_ID);
  if (!el) {
    el = document.createElement("div");
    el.id = PROGRESS_OVERLAY_ID;
    document.body.appendChild(el);
  }
  el.innerHTML = buildProgressHtml(percent, statusText);
}

function hideProgressOverlay() {
  const el = document.getElementById(PROGRESS_OVERLAY_ID);
  if (el && el.parentNode) {
    el.parentNode.removeChild(el);
  }
}

async function showDownloadError(error: any) {
  hideProgressOverlay();
  const msg =
    typeof error === "string"
      ? error
      : error?.message || error?.exception || JSON.stringify(error);
  const alert = await alertController.create({
    header: "错误提示",
    message: "升级文件下载失败！<br>" + msg,
    backdropDismiss: false,
    buttons: ["确定"],
  });
  await alert.present();
}

async function showFeedback(version: any) {
  const alert = await alertController.create({
    header: "提示",
    message: "当前已经是最新版本" + version,
    backdropDismiss: false,
    buttons: [{ text: "确定" }],
  });
  await alert.present();
}

function openApp(path: string) {
  showProgressOverlay(100, "正在打开安装程序...");
  FileOpener.showOpenWithDialog(path, "application/vnd.android.package-archive")
    .then(() => {
      hideProgressOverlay();
      console.log("文件打开成功");
    })
    .catch(async (e) => {
      hideProgressOverlay();
      console.log("文件打开失败", e);
      const alert = await alertController.create({
        header: "错误提示",
        message: "安装包已下载，但打开安装程序失败，请到系统下载目录手动安装。",
        backdropDismiss: false,
        buttons: ["确定"],
      });
      await alert.present();
    });
}

function resolveDownloadDir(): string {
  // 应用专属外部目录，Android 10+ 不需要 WRITE_EXTERNAL_STORAGE
  const externalData = (File as any).externalDataDirectory || (window as any).cordova?.file?.externalDataDirectory;
  const dataDir = (File as any).dataDirectory || (window as any).cordova?.file?.dataDirectory;
  return externalData || dataDir || "";
}

function downloadApp(fileName: string, fileURL: string) {
  const downloadDir = resolveDownloadDir();
  if (!downloadDir) {
    showDownloadError("无法获取本地存储目录");
    return;
  }

  const targetPath = downloadDir + fileName;
  let lastPercent = -1;

  showProgressOverlay(0, "开始下载安装包...");

  try {
    const fileTransfer = FileTransfer.create();
    fileTransfer.onProgress((progressEvent: any) => {
      if (!progressEvent.lengthComputable || !progressEvent.total) {
        showProgressOverlay(0, "正在下载安装包...");
        return;
      }
      const percent = Math.floor((progressEvent.loaded / progressEvent.total) * 100);
      if (percent !== lastPercent) {
        lastPercent = percent;
        showProgressOverlay(percent, "正在下载安装包，请保持网络畅通");
      }
    });

    fileTransfer
      .download(fileURL, targetPath, true)
      .then((entry: any) => {
        const path = entry?.nativeURL || entry?.toURL?.() || targetPath;
        showProgressOverlay(100, "下载完成，准备安装...");
        setTimeout(() => openApp(path), 400);
      })
      .catch(async (error: any) => {
        await showDownloadError(error);
        console.error(error);
      });
  } catch (e) {
    showDownloadError(e);
  }
}

const STORAGE_KEY_VERSION_CODE = "STORAGE_VERSION_CODE";

export default {
  clearStorageVersion() {
    localStorage.removeItem(STORAGE_KEY_VERSION_CODE);
  },

  getLastVersion() {
    return new Promise((resolve) => {
      axios
        .post(`/checkLastVersionNew?domainName=${myConfig.config.hostname}`)
        .then(async (res) => {
          resolve(res);
        });
    });
  },

  async checkVersion(feedback: boolean) {
    const appCode = await AppVersion.getVersionCode();

    let version = parseInt(String(appCode));
    const storageVersionCode = localStorage.getItem(STORAGE_KEY_VERSION_CODE);

    if (storageVersionCode != null) {
      if (parseInt(storageVersionCode) > version) {
        version = parseInt(storageVersionCode);
      }
    }

    const res: any = await this.getLastVersion();

    if (res.data.success) {
      if (version < res.data.minVersionCode) {
        const alert = await alertController.create({
          header: "版本更新",
          message: "请更新至最新版本:" + res.data.lastVersionName,
          backdropDismiss: false,
          buttons: [
            {
              text: "确定",
              handler: () => {
                downloadApp(res.data.fileName, res.data.fileURL);
              },
            },
          ],
        });
        await alert.present();
      } else if (version < res.data.lastVersionCode) {
        const alert = await alertController.create({
          header: "更新提醒",
          message: "是否更新至新版本？" + res.data.lastVersionName,
          backdropDismiss: false,
          buttons: [
            {
              text: "稍后提醒",
            },
            {
              text: "忽略此次更新",
              handler: () => {
                localStorage.setItem(STORAGE_KEY_VERSION_CODE, res.data.lastVersionCode);
              },
            },
            {
              text: "立即升级",
              handler: () => {
                downloadApp(res.data.fileName, res.data.fileURL);
              },
            },
          ],
        });
        await alert.present();
      } else {
        if (feedback) {
          await showFeedback(await initSystemVersion());
        }
      }
    }
  },
};
